import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Auth_SignInInput,
  EmailVerificationType,
  useAuth_AuthenticateMutation,
  useAuth_SignInMutation,
} from 'queries';
import { X } from 'ts-react-feather-icons';
import { useAnalytics } from 'use-analytics';
import userflow from 'userflow.js';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { AnalyticsEvent, NodeEnvironment } from 'utils/enum';
import { clearAllGridStates } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { LocalStorageKeys, useLocalStorage } from 'utils/useLocalStorage';

const useStyles = makeStyles(() => ({
  input: {
    marginTop: '25px',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  dialog: {
    textAlign: 'center',
  },
  dialogTitleText: {
    display: 'inline-block',
    marginRight: '5px',
  },
  dialogTitle: {
    paddingTop: '35px',
  },
  dialogContent: {
    paddingLeft: '15%',
    paddingRight: '15%',
  },
  dialogActions: { textAlign: 'center', marginTop: '50px', marginBottom: '25px' },
  exitDialogIcon: { display: 'inline-block', right: '15px', top: '15px', position: 'absolute' },
  resetLink: {
    color: '#44D2F2',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginBottom: '63px',
  },
}));

const schema = yup.object().shape({
  code: yup
    .string()
    .required('Please enter your verification code')
    .min(6, 'Verification code must be at least 6 characters'),
});

type Props = {
  open: boolean;
  credentials: { email: string; password: string } | null;
  onClose: () => void;
};

const MFAPrompt = ({ open, credentials, onClose }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const analytics = useAnalytics();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsInviteTeamMemberBannerVisible] = useLocalStorage(
    LocalStorageKeys.InviteTeamMemberBanner,
    true,
  );

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<Auth_SignInInput>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
  });

  const [signIn, { loading: signInExecuting }] = useAuth_SignInMutation({
    onCompleted: (data) => {
      if (data.data.errorPasswordExpired) {
        history.push({
          pathname: '/reset-password',
          state: {
            title: 'Your password has expired',
          },
        });
        return;
      } else if (data.data.errorEmailNotVerified) {
        history.push({
          pathname: '/verify-email',
          state: {
            verificationType: EmailVerificationType.UserRegistration,
            email: credentials?.email,
          },
        });
        return;
      }

      if (!data.data.user) {
        showAlert({
          message: 'Error retrieving user',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      analytics.identify(data.data.user.id, {
        $name: data.data.user.firstName + ' ' + data.data.user.lastName,
        $email: data.data.user.email,
        Phone: data.data.user.phone,
        'Portal ID': data.data.portal ? data.data.portal.id : 'None',
        'Portal Name': data.data.portal ? data.data.portal.name : 'None',
        Subscription: data.data.featureSubscriptionType || 'None',
      });
      analytics.track(AnalyticsEvent.LOGIN);

      if (
        process.env.REACT_APP_USERFLOW_TOKEN &&
        (process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT ||
          process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD)
      ) {
        // Reset Userflow user if one is present because they will be identified on the home page
        userflow.reset();
      }

      clearAllGridStates();
      setIsInviteTeamMemberBannerVisible(true);
      history.push('/home');
    },
    onError: (e) => {
      showAlert({
        message: `Error verifying your identity: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      });

      onClose();
    },
  });

  const [authenticate] = useAuth_AuthenticateMutation({
    onError: (e) => {
      showAlert({
        message: `Error verifying your identity: ${e.message}`,
        severity: NotificationSeverity.ERROR,
      });

      onClose();
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (formData: any) => {
    await signIn({ variables: { input: { ...formData, ...credentials } } });
  };

  return (
    <Dialog open={open} className={classes.dialog} onClose={onClose}>
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.dialogTitleText}>Verify Your Identity</Typography>
        <IconButton className={classes.exitDialogIcon} onClick={onClose} size="large">
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {errors.code && (
            <span className={classes.inputError}>
              <Typography>{errors.code.message}</Typography>
            </span>
          )}
          <Controller
            name="code"
            control={control}
            render={({ field: { ref, name, onBlur, onChange, value } }) => (
              <FormTextField
                name={name}
                defaultValue={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
                autoFocus
                placeholder="000-000"
                errorMessage={errors.code?.message}
                label="For additional security, please verify your identity. Please enter the code we sent to the
                phone number attached to your account."
                inputProps={{ style: { textAlign: 'center' } }}
                data-cy="verification-input"
              />
            )}
          />
          <div className={classes.dialogActions}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isSubmitting || signInExecuting}
            >
              Verify
            </Button>
          </div>
        </form>
        <Typography
          onClick={() =>
            authenticate({
              variables: {
                email: credentials!.email,
              },
            })
          }
          className={classes.resetLink}
        >
          Resend the code
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default MFAPrompt;
